import React, { useState, useEffect, useRef } from 'react';
import theme from '../styles/theme';
import { useMobile } from '../contexts/MobileContext';

const GRID_BORDER = 2;
const CELL_BORDER = 1;
const PATTERN_SIZE = 5; // Match the editor pattern size

// Enhanced earthquake animations
const earthquakeKeyframes = `
@keyframes shake {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  10% { transform: translate(-5px, -5px) rotate(-2deg); }
  20% { transform: translate(5px, 5px) rotate(2deg); }
  30% { transform: translate(-5px, 5px) rotate(-1deg); }
  40% { transform: translate(5px, -5px) rotate(1deg); }
  50% { transform: translate(-5px, 5px) rotate(-2deg); }
  60% { transform: translate(5px, 5px) rotate(0deg); }
  70% { transform: translate(-5px, -5px) rotate(2deg); }
  80% { transform: translate(5px, -5px) rotate(-2deg); }
  90% { transform: translate(-5px, 5px) rotate(1deg); }
}

@keyframes crumble {
  0% { 
    transform: scale(1) rotate(0deg); 
    opacity: 1;
    filter: brightness(1.5);
  }
  50% { 
    transform: scale(0.8) rotate(15deg); 
    opacity: 0.7;
    filter: brightness(2);
  }
  100% { 
    transform: scale(0) rotate(30deg); 
    opacity: 0;
    filter: brightness(2.5);
  }
}

@keyframes ripple {
  0% { 
    transform: translate(-50%, -50%) scale(0.3);
    opacity: 1;
    border-width: 10px;
  }
  100% { 
    transform: translate(-50%, -50%) scale(3);
    opacity: 0;
    border-width: 1px;
  }
}

@keyframes flash {
  0% { background-color: transparent; }
  25% { background-color: rgba(255, 0, 0, 0.4); }
  50% { background-color: rgba(255, 120, 0, 0.3); }
  75% { background-color: rgba(255, 180, 0, 0.2); }
  100% { background-color: transparent; }
}

@keyframes shockwave {
  0% { 
    box-shadow: 0 0 0 0px rgba(255, 0, 0, 0.5);
    background-color: rgba(255, 0, 0, 0.5);
  }
  70% { 
    box-shadow: 0 0 0 20px rgba(255, 0, 0, 0);
    background-color: rgba(255, 0, 0, 0.1);
  }
  100% { 
    box-shadow: 0 0 0 0px rgba(255, 0, 0, 0);
    background-color: rgba(255, 0, 0, 0);
  }
}`;

const getCellColor = (playerId) => {
  const colors = [
    theme.colors.card, // Empty cell
    '#FF4136', // Player 1 - Red
    '#2ECC40', // Player 2 - Green
    '#0074D9', // Player 3 - Blue
    '#FFDC00', // Player 4 - Yellow
    '#B10DC9', // Player 5 - Purple
    '#FF851B', // Player 6 - Orange
    '#7FDBFF', // Player 7 - Light Blue
    '#01FF70'  // Player 8 - Lime
  ];
  return colors[playerId] || colors[0];
};

// Enhanced earthquake effect check
const isInEarthquakeArea = (i, j, earthquake) => {
  if (!earthquake) return false;
  const dx = Math.abs(i - earthquake.x);
  const dy = Math.abs(j - earthquake.y);
  
  // Different intensities based on distance from epicenter
  if (dx <= 2 && dy <= 2) {
    return 'epicenter'; // Epicenter - strongest effect (5x5 core area)
  } else if (dx <= 3 && dy <= 3) {
    return 'strong'; // Strong effect area
  } else if (dx <= 4 && dy <= 4) {
    return 'moderate'; // Moderate effect area - visual only
  }
  return false;
};

function Grid({ grid, onCellClick, selectedPosition, earthquake, pattern }) {
  const [hoverPosition, setHoverPosition] = useState(null);
  const [isBlinking, setIsBlinking] = useState(false);
  const [cellSize, setCellSize] = useState(20); // Default, will be adjusted
  const gridContainerRef = useRef(null);
  const gridRef = useRef(null);
  const { isMobile } = useMobile();

  // Set up non-passive touch event handlers
  useEffect(() => {
    if (!gridRef.current || !isMobile) return;

    // Add event listeners with { passive: false } option
    const gridElement = gridRef.current;
    
    const handleTouchMove = (e) => {
      // Clear hover position during scroll/drag
      setHoverPosition(null);
      // Can safely prevent default now
      e.preventDefault();
    };

    // Add the touch move listener to the grid container with passive: false
    gridElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    // Cleanup
    return () => {
      gridElement.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isMobile, gridRef]);

  // Clear hover position when selected position changes (pattern is placed)
  useEffect(() => {
    if (selectedPosition) {
      setHoverPosition(null);
    }
  }, [selectedPosition]);

  // Debug patterns
  useEffect(() => {
    console.log("Grid received pattern:", pattern);
    console.log("Has cells:", pattern && pattern.some(row => row.some(cell => cell === 1)));
  }, [pattern]);

  if (!grid || !grid[0]) return null;

  const height = grid.length;
  const width = grid[0].length;

  // Calculate cell size based on viewport height
  useEffect(() => {
    const updateCellSize = () => {
      if (!gridContainerRef.current) return;
      
      // Get the viewport height
      const viewportHeight = window.innerHeight;
      // Reserve space for padding, borders and other UI elements (adjust as needed)
      const reservedSpace = 120; // Estimated space for padding, borders, etc.
      // Available height for the grid
      const availableHeight = viewportHeight - reservedSpace;
      // Calculate cell size based on grid height
      const calculatedCellSize = Math.floor(availableHeight / height);
      
      // Set a minimum and maximum size for cells
      const newCellSize = Math.max(10, Math.min(calculatedCellSize, 40));
      setCellSize(newCellSize);
    };

    // Initial calculation
    updateCellSize();
    
    // Update on window resize
    window.addEventListener('resize', updateCellSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateCellSize);
  }, [height]);

  // Function to get wrapped coordinates
  const getWrappedCoords = (x, y) => ({
    x: ((x % height) + height) % height,
    y: ((y % width) + width) % width
  });

  // Function to get cell value with wrap-around
  const getCellValue = (i, j) => {
    const coords = getWrappedCoords(i, j);
    return grid[coords.x][coords.y];
  };

  // Handle cell click with blinking effect
  const handleCellClick = (i, j) => {
    console.log("Cell clicked at", i, j);
    setIsBlinking(true);
    
    // Reset blinking after animation
    setTimeout(() => {
      setIsBlinking(false);
    }, 1000);
    
    // Clear any hover position (for mobile devices)
    if (isMobile) {
      setHoverPosition(null);
    }
    
    console.log("Calling onCellClick with:", i, j);
    onCellClick(i, j);
    console.log("onCellClick called");
  };

  // Handle touch events for mobile - without preventDefault in the React handlers
  const handleTouchStart = (i, j) => {
    if (isMobile) {
      setHoverPosition({ x: i, y: j });
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) {
      // Immediately clear hover position on touch end
      setHoverPosition(null);
    }
  };

  // Check if a cell is within the pattern area and has a live cell in the pattern
  const isInLivePatternCell = (baseI, baseJ, i, j) => {
    if (!pattern) return false;
    const offsetI = i - baseI;
    const offsetJ = j - baseJ;
    
    // Check if in bounds of pattern and the pattern cell is alive
    if (offsetI >= 0 && offsetI < 5 && offsetJ >= 0 && offsetJ < 5) {
      return pattern[offsetI][offsetJ] === 1;
    }
    return false;
  };

  // Check if a cell is within the pattern area
  const isInPatternArea = (baseI, baseJ, i, j) => {
    // Ensure we have valid base coordinates
    if (baseI === undefined || baseJ === undefined) return false;
    
    const offsetI = i - baseI;
    const offsetJ = j - baseJ;
    return offsetI >= 0 && offsetI < PATTERN_SIZE && offsetJ >= 0 && offsetJ < PATTERN_SIZE;
  };

  return (
    <>
      <style>{earthquakeKeyframes}</style>
      <div ref={gridContainerRef} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing.md,
        height: '100%',
        minHeight: '300px',
        marginBottom: theme.spacing.md,
        position: 'relative',
      }}>
        {/* Add earthquake flash effect container */}
        {earthquake && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            animation: 'flash 0.5s ease-out',
            zIndex: 1,
            pointerEvents: 'none',
          }} />
        )}
        <div ref={gridRef} style={{
          display: 'inline-block',
          border: `${GRID_BORDER}px solid ${theme.colors.primary}`,
          borderRadius: theme.borders.radius.md,
          backgroundColor: theme.colors.background,
          padding: theme.spacing.sm,
          boxShadow: earthquake ? `0 0 20px rgba(255, 0, 0, 0.5)` : theme.borders.shadow.large,
          position: 'relative',
          overflow: 'hidden',
          animation: earthquake ? 'shake 0.5s ease-in-out infinite' : 'none',
          transition: 'box-shadow 0.3s ease',
        }}>
          {/* Main grid */}
          <div style={{ position: 'relative' }}>
            {grid.map((row, i) => (
              <div key={i} style={{ display: 'flex' }}>
                {row.map((_, j) => {
                  const cell = getCellValue(i, j);
                  const isHovered = hoverPosition && isInPatternArea(hoverPosition.x, hoverPosition.y, i, j);
                  const isSelected = selectedPosition && isInPatternArea(selectedPosition.x, selectedPosition.y, i, j);
                  const isEarthquake = isInEarthquakeArea(i, j, earthquake);
                  
                  return (
                    <div
                      key={j}
                      onClick={() => handleCellClick(i, j)}
                      onMouseEnter={() => !isMobile && setHoverPosition({ x: i, y: j })}
                      onMouseLeave={() => !isMobile && setHoverPosition(null)}
                      onTouchStart={() => handleTouchStart(i, j)}
                      onTouchEnd={handleTouchEnd}
                      onTouchCancel={handleTouchEnd}
                      style={{
                        width: cellSize,
                        height: cellSize,
                        backgroundColor: getCellColor(cell),
                        border: `${CELL_BORDER}px solid ${
                          isInEarthquakeArea(i, j, earthquake) === 'epicenter' ? 'rgba(255, 0, 0, 0.8)' :
                          isInEarthquakeArea(i, j, earthquake) === 'strong' ? 'rgba(255, 80, 0, 0.6)' :
                          isInEarthquakeArea(i, j, earthquake) === 'moderate' ? 'rgba(255, 150, 0, 0.4)' :
                          isSelected ? theme.colors.primary : 
                          isHovered ? `${theme.colors.primary}66` : 
                          theme.colors.card
                        }`,
                        borderRadius: theme.borders.radius.sm,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        transform: isHovered ? 'scale(1.05)' : (cell !== 0 ? 'scale(0.95)' : 'scale(1)'),
                        boxShadow: isInEarthquakeArea(i, j, earthquake) === 'epicenter' ? '0 0 10px rgba(255, 0, 0, 0.7)' :
                                isInEarthquakeArea(i, j, earthquake) === 'strong' ? '0 0 8px rgba(255, 80, 0, 0.5)' :
                                isInEarthquakeArea(i, j, earthquake) === 'moderate' ? '0 0 6px rgba(255, 150, 0, 0.3)' :
                                isSelected ? `0 0 10px ${theme.colors.primary}` :
                                isHovered ? `0 0 10px ${theme.colors.primary}66` :
                                cell !== 0 ? theme.borders.shadow.small : 'none',
                        position: 'relative',
                        zIndex: isInEarthquakeArea(i, j, earthquake) ? 2 : (isHovered ? 3 : 1),
                        animation: isInEarthquakeArea(i, j, earthquake) === 'epicenter' ? 'crumble 1s forwards' : 
                                isInEarthquakeArea(i, j, earthquake) === 'strong' ? 'shake 0.7s ease-in-out' :
                                isInEarthquakeArea(i, j, earthquake) === 'moderate' ? 'shake 0.5s ease-in-out' :
                                isSelected && isBlinking ? 'blink 0.5s ease-in-out 2' : 
                                'none',
                      }}
                    >
                      {/* Show ghost pattern cells when hovering */}
                      {hoverPosition && pattern && isInLivePatternCell(hoverPosition.x, hoverPosition.y, i, j) && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: `${theme.colors.primary}33`,
                          borderRadius: 'inherit',
                          zIndex: 2,
                          boxShadow: `inset 0 0 5px ${theme.colors.primary}66`
                        }} />
                      )}
                      {isInEarthquakeArea(i, j, earthquake) === 'epicenter' && (
                        <>
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              width: '200%',
                              height: '200%',
                              border: '3px solid rgba(255, 0, 0, 0.8)',
                              borderRadius: '50%',
                              animation: 'ripple 1s ease-out infinite',
                              pointerEvents: 'none',
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              width: '150%',
                              height: '150%',
                              border: '2px solid rgba(255, 0, 0, 0.6)',
                              borderRadius: '50%',
                              animation: 'ripple 1s ease-out infinite 0.2s',
                              pointerEvents: 'none',
                            }}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Visual indicators for wrap-around edges */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: theme.spacing.xs,
            background: `linear-gradient(to bottom, ${theme.colors.primary}33, transparent)`,
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: theme.spacing.xs,
            background: `linear-gradient(to top, ${theme.colors.primary}33, transparent)`,
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: theme.spacing.xs,
            background: `linear-gradient(to right, ${theme.colors.primary}33, transparent)`,
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: theme.spacing.xs,
            background: `linear-gradient(to left, ${theme.colors.primary}33, transparent)`,
            pointerEvents: 'none',
          }} />
        </div>
      </div>
    </>
  );
}

export default Grid;